import React from "react";
import { Sync } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, LinearProgress } from "@mui/material";
import type { SingleFileProps } from "@components/FileUpload/FileUpload.types";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import OverflowTooltip from "@components/OverflowTooltip/OverflowTooltip";
import { getFileTitle } from "@features/port-security/components/utils";
import { useTranslation } from "react-i18next";

const SingleFile: React.FC<SingleFileProps> = ({
  file,
  testId,
  fieldName,
  currentFileIndex,
  setFiles,
  removeFn,
  isLoading,
  disabled,
  onView,
  viewLabel,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center bg-[#F7F8FA] border border-[#E6EDF2] rounded px-4 h-[70px] w-full overflow-hidden"
      data-testid={`${testId}-single-file`}
    >
      <div className="flex items-center w-full gap-2">
        {isLoading && (
          <Sync
            sx={{
              "mr": 1,
              "animation": "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(-360deg)" },
              },
            }}
          />
        )}

        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center min-w-0">
            <div className="flex-1 w-0 min-w-0 pr-4 pb-1 overflow-hidden">
              <span className="block text-[#0A46B7] text-sm truncate">
                <OverflowTooltip>{getFileTitle(file?.name, file?.type)}</OverflowTooltip>
              </span>
            </div>

            <div className="flex items-center ml-auto shrink-0 gap-2">
              {/* DFT-0088 (AD2): additive, optional view action - rendered before Remove, matching
                  Pre-approve's HOD document's existing View -> Remove order. Independent of
                  `disabled` (a read action, not a mutation), only hidden while loading like
                  Remove. */}
              {onView && !isLoading && (
                <button
                  type="button"
                  data-testid={`${testId}-view`}
                  className="cursor-pointer shrink-0"
                  aria-label={viewLabel || t("common.view", { defaultValue: "View" })}
                  onClick={onView}
                >
                  <VisibilityIcon className="color-[#646A77]" />
                </button>
              )}

              {!isLoading && !disabled && (
                <button
                  type="button"
                  data-testid={`${testId}-delete`}
                  className="cursor-pointer shrink-0"
                  onClick={() => {
                    setFiles();
                    if (typeof currentFileIndex === "number") {
                      useUploadStore.getState().removeFileAtIndex(fieldName, currentFileIndex);
                    } else {
                      useUploadStore.getState().clearFilesForKey(fieldName);
                    }
                    if (removeFn) removeFn();
                  }}
                >
                  <ClearIcon className="color-[#646A77]" data-testid={`${testId}-delete-icon`} />
                </button>
              )}
            </div>
          </div>

          {isLoading && (
            <Box>
              <LinearProgress />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleFile;
